
import { EditorContent, useEditor } from "@tiptap/react";
import { Button } from "./button";
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading2,
  Undo,
  Redo,
  ImagePlus,
} from "lucide-react";

import StarterKit from "@tiptap/starter-kit";
import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import HardBreak from "@tiptap/extension-hard-break";
import BulletList from "@tiptap/extension-bullet-list";
import ListItem from "@tiptap/extension-list-item";
import OrderedList from "@tiptap/extension-ordered-list";

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
}

const RichTextEditor = ({ value, onChange }: RichTextEditorProps) => {
  const editor = useEditor({
    extensions: [
      Document,
      Paragraph,
      Text,
      HardBreak,
      ListItem,
      BulletList.configure({
        keepMarks: true,
        keepAttributes: true,
        HTMLAttributes: {
          class: 'list-disc ml-4',
        },
      }),
      OrderedList.configure({
        keepMarks: true,
        keepAttributes: true,
        HTMLAttributes: {
          class: 'list-decimal ml-4',
        },
      }),
      StarterKit.configure({
        bulletList: false,
        orderedList: false,
        history: {
          depth: 100,
          newGroupDelay: 500,
        },
        heading: {
          levels: [2],
        },
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm focus:outline-none min-h-[200px] h-full w-full',
      },
    },
  });

  const addImage = () => {
    const url = window.prompt("URL de l'image :");
    if (url && editor) {
      editor.chain().focus().insertContent(`<img src="${url}" alt="Image" style="max-width:100%;border-radius:8px;margin:16px 0;" />`).run();
    }
  };

  if (!editor) {
    return null;
  }

  return (
    <div className="border border-input rounded-md overflow-hidden flex flex-col">
      <div className="border-b border-input p-2 flex flex-wrap gap-2 bg-background">
        <Button type="button" variant={editor.isActive("bold") ? "default" : "outline"} size="icon" onClick={() => editor.chain().focus().toggleBold().run()} title="Gras">
          <Bold className="h-4 w-4" />
        </Button>
        <Button type="button" variant={editor.isActive("italic") ? "default" : "outline"} size="icon" onClick={() => editor.chain().focus().toggleItalic().run()} title="Italique">
          <Italic className="h-4 w-4" />
        </Button>
        <Button type="button" variant={editor.isActive("heading", { level: 2 }) ? "default" : "outline"} size="icon" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Titre">
          <Heading2 className="h-4 w-4" />
        </Button>
        <Button type="button" variant={editor.isActive("bulletList") ? "default" : "outline"} size="icon" onClick={() => editor.chain().focus().toggleBulletList().run()} title="Liste à puces">
          <List className="h-4 w-4" />
        </Button>
        <Button type="button" variant={editor.isActive("orderedList") ? "default" : "outline"} size="icon" onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Liste numérotée">
          <ListOrdered className="h-4 w-4" />
        </Button>

        <div className="w-px bg-border mx-1" />

        <Button type="button" variant="outline" size="icon" onClick={addImage} title="Insérer une image">
          <ImagePlus className="h-4 w-4" />
        </Button>

        <div className="w-px bg-border mx-1" />

        <Button type="button" variant="outline" size="icon" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Annuler">
          <Undo className="h-4 w-4" />
        </Button>
        <Button type="button" variant="outline" size="icon" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Rétablir">
          <Redo className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex-grow relative min-h-[300px]">
        <EditorContent
          editor={editor}
          className="p-3 h-full w-full focus:outline-none prose prose-sm max-w-none [&_img]:my-4 [&_img]:rounded-lg [&_img]:max-w-full"
        />
      </div>
    </div>
  );
};

export default RichTextEditor;
